import { useState, useRef } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAssetUploadAccept, type AssetUploadKind, validateAssetFiles } from '@/lib/assetUpload'

interface FileUploadZoneProps {
  onUpload: (files: File[]) => void
  isUploading: boolean
  allowedAssetType?: AssetUploadKind
}

export function FileUploadZone({
  onUpload,
  isUploading,
  allowedAssetType = 'all',
}: FileUploadZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading) {
      setIsDragActive(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    if (isUploading) return

    const files = Array.from(e.dataTransfer.files)
    const validFiles = validateAssetFiles(files, allowedAssetType)
    if (validFiles.length > 0) {
      onUpload(validFiles)
    }
  }

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = validateAssetFiles(files, allowedAssetType)
    if (validFiles.length > 0) {
      onUpload(validFiles)
    }
    // Reset input to allow re-uploading same file
    e.target.value = ''
  }

  return (
    <div
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all bg-white',
        isDragActive
          ? 'border-primary bg-primary/5'
          : 'border-gray-300 hover:border-gray-400',
        isUploading && 'opacity-50 cursor-not-allowed'
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={getAssetUploadAccept(allowedAssetType)}
        multiple
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      <div className="flex flex-col items-center gap-3">
        <div
          className={cn(
            'p-3 rounded-full',
            isDragActive ? 'bg-primary/10' : 'bg-gray-100'
          )}
        >
          <Upload
            className={cn('h-8 w-8', isDragActive ? 'text-primary' : 'text-gray-400')}
          />
        </div>
        <div>
          <p className="font-medium text-gray-700">
            {isDragActive ? 'Drop files here' : 'Click to upload or drag and drop'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {allowedAssetType === 'image'
              ? 'Images up to 50MB'
              : allowedAssetType === 'video'
                ? 'Videos up to 50MB'
                : 'Images and videos up to 50MB'}
          </p>
        </div>
      </div>
    </div>
  )
}
